import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { usePlatformSettings } from "@/hooks/usePlatformSettings";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const platform = usePlatformSettings();
  const [email, setEmail] = useState("demo@bi.com");
  const [password, setPassword] = useState("demo123");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!", { duration: 1500 });
      nav("/");
    } catch (err) {
      // Surface the Retry-After from a 429 lockout with a friendly minutes/seconds label
      // so the user knows exactly how long the account is throttled.
      const status = err?.response?.status;
      const retry = Number(err?.response?.headers?.["retry-after"]);
      if (status === 429 && retry > 0) {
        const mins = Math.ceil(retry / 60);
        const label = mins > 1 ? `~${mins} minutes` : `about a minute`;
        toast.error(`Too many failed attempts. Please try again in ${label}.`);
      } else {
        toast.error(err?.response?.data?.detail || "Login failed");
      }
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex bi-canvas">
      <div className="hidden lg:flex flex-col justify-between w-[46%] hero-gradient p-12 relative overflow-hidden">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-[color:var(--brand-ocean)] flex items-center justify-center"><Sparkles className="w-5 h-5 text-white" /></div>
          <span className="font-display text-lg font-semibold text-[color:var(--ink)]">Eazy Analytics</span>
        </div>
        <div>
          <h1 className="font-display text-4xl xl:text-5xl tracking-tight text-[color:var(--ink)] leading-[1.1]">Turn raw data into<br/>beautiful insight.</h1>
          <p className="text-[color:var(--ink-2)] mt-4 max-w-md">Upload files or connect databases, build drag-and-drop dashboards, and just ask your data questions in plain English.</p>
          <div className="flex gap-3 mt-8">
            {["Drag & drop builder","AI ask-your-data","25+ widgets"].map((t) => (
              <span key={t} className="glass-card px-3 py-1.5 text-xs font-medium text-[color:var(--ink-2)]">{t}</span>
            ))}
          </div>
        </div>
        <div className="text-xs text-[color:var(--ink-3)]">© Eazy Analytics</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8"><div className="w-9 h-9 rounded-xl bg-[color:var(--brand-ocean)] flex items-center justify-center"><Sparkles className="w-5 h-5 text-white" /></div><span className="font-display text-lg font-semibold">Eazy Analytics</span></div>
          <h2 className="font-display text-2xl text-[color:var(--ink)]">Sign in</h2>
          <p className="text-sm text-[color:var(--ink-3)] mt-1">Welcome back. Let's explore your data.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label className="text-[color:var(--ink-2)]">Email</Label>
              <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 bg-[color:var(--surface)]" required />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-[color:var(--ink-2)]">Password</Label>
                <Link to="/forgot-password" data-testid="login-forgot-link" className="text-xs text-[color:var(--brand-ocean)] font-medium hover:underline">Forgot password?</Link>
              </div>
              <div className="relative mt-1.5">
                <Input
                  data-testid="login-password"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-[color:var(--surface)] pr-10"
                  required
                />
                <button
                  type="button"
                  data-testid="login-password-toggle"
                  onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-[color:var(--ink-3)] hover:text-[color:var(--ink)]"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full bg-[color:var(--brand-ocean)] hover:bg-[#1E3AD9] text-white">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>
          <p className="text-sm text-[color:var(--ink-3)] mt-5 text-center">
            {platform?.signup_disabled ? (
              <span data-testid="login-signup-disabled" className="text-[color:var(--ink-3)]">Signups are currently disabled by the platform administrator.</span>
            ) : (
              <>No account? <Link to="/signup" data-testid="login-signup-link" className="text-[color:var(--brand-ocean)] font-medium">Create one</Link></>
            )}
          </p>
          <div className="mt-4 text-xs text-center text-[color:var(--ink-3)] glass-card py-2">Demo: demo@bi.com / demo123</div>
        </div>
      </div>
    </div>
  );
}
